import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiSearch, FiEye, FiX, FiChevronLeft, FiChevronRight, FiMail, FiCalendar, FiCreditCard, FiMessageSquare, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { getUsersForManagement, getUserDetails, getUserConversations, getConversationMessages } from '../services/userManagementService';
import Spinner from '../components/Spinner';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [lastUserId, setLastUserId] = useState(null);
  const [pageHistory, setPageHistory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const usersPerPage = 50;

  // Fetch users
  const fetchUsers = useCallback(async (cursor = null, searchTerm = search) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getUsersForManagement({
        lastUserId: cursor,
        limit: usersPerPage,
        search: searchTerm || null
      });

      setUsers(result.users || []);
      setHasMore(result.pagination?.hasMore || false);
      setLastUserId(result.pagination?.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  // Initial load
  useEffect(() => {
    fetchUsers(null, search);
    setPageHistory([]);
  }, [search]);

  // Handle search
  const handleSearch = () => {
    setSearch(searchInput.toLowerCase());
    setLastUserId(null);
    setPageHistory([]);
  };

  // Handle search key press
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle pagination - Next
  const handleNextPage = () => {
    if (hasMore && lastUserId) {
      setPageHistory(prev => [...prev, { cursor: lastUserId, users: [...users] }]);
      fetchUsers(lastUserId, search);
    }
  };

  // Handle pagination - Previous
  const handlePrevPage = () => {
    if (pageHistory.length > 0) {
      const newHistory = [...pageHistory];
      const previousPage = newHistory.pop();
      setPageHistory(newHistory);
      setUsers(previousPage.users);
      setLastUserId(previousPage.cursor);
      setHasMore(true);
    }
  };

  // View user details
  const handleViewDetails = async (userId) => {
    setLoadingDetails(true);
    try {
      const details = await getUserDetails(userId);
      setSelectedUser(details);
      // Reset conversation state when viewing a new user
      setConversations([]);
      setShowConversations(false);
      setSelectedConversation(null);
      setMessages([]);
    } catch (err) {
      console.error('Error fetching user details:', err);
      alert('Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fetch user conversations
  const handleFetchConversations = async () => {
    if (!selectedUser?.uid) return;
    
    setLoadingConversations(true);
    try {
      const result = await getUserConversations(selectedUser.uid, { limit: 50 });
      setConversations(result.conversations || []);
      setShowConversations(true);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      alert('Failed to load conversations');
    } finally {
      setLoadingConversations(false);
    }
  };

  // View conversation messages
  const handleViewMessages = async (conversationId) => {
    if (!selectedUser?.uid) return;
    
    setLoadingMessages(true);
    try {
      const result = await getConversationMessages(selectedUser.uid, conversationId, { limit: 100 });
      setMessages(result.messages || []);
      setSelectedConversation(conversationId);
    } catch (err) {
      console.error('Error fetching messages:', err);
      alert('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Close messages modal
  const handleCloseMessages = () => {
    setSelectedConversation(null);
    setMessages([]);
  };

  // Close details modal
  const handleCloseDetails = () => {
    setSelectedUser(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'trial':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FiUsers className="mr-3 text-blue-600" />
            User Management
          </h1>
          <p className="mt-2 text-gray-600">Manage and view all user accounts</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FiSearch />
              Search
            </button>
            {search && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setLastUserId(null);
                  setPageHistory([]);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Clear
              </button>
            )}
          </div>
          {search && (
            <p className="text-xs text-gray-600 mt-2">
              Searching for: <strong>{search}</strong>
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="relative">
              {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10 rounded-lg">
                  <Spinner />
                </div>
              )}
              
              {users.length === 0 && !loading ? (
                <div className="text-center py-12 text-gray-500">
                  No users found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subscription
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversations
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Login
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map(user => (
                        <tr key={user.uid} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleViewDetails(user.uid)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline text-left"
                            >
                              {user.displayName || 'No name'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.email}</div>
                            {!user.emailNotificationsEnabled && (
                              <span className="text-xs text-gray-500">(opted out)</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(user.subscriptionStatus)}`}>
                              {user.subscriptionStatus || 'inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-l border-r border-gray-100 font-semibold bg-gray-50/50 text-center">
                            {user.conversationCount ?? 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.lastLoginAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {(hasMore || pageHistory.length > 0) && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-700">
                  Showing {users.length} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={pageHistory.length === 0 || loading}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasMore || loading}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <FiUsers className="mr-2 text-blue-600" />
                User Details
              </h2>
              <button
                onClick={handleCloseDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            {loadingDetails ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : (
              <div className="p-6">
                {/* User Profile Section */}
                <div className="flex items-start mb-6 pb-6 border-b border-gray-200">
                  <div className="flex-shrink-0">
                    {selectedUser.photoURL ? (
                      <img
                        className="h-20 w-20 rounded-full"
                        src={selectedUser.photoURL}
                        alt={selectedUser.displayName}
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-2xl font-semibold">
                        {selectedUser.displayName ? selectedUser.displayName[0].toUpperCase() : '?'}
                      </div>
                    )}
                  </div>
                  <div className="ml-6 flex-1">
                    <h3 className="text-2xl font-bold text-gray-900">{selectedUser.displayName || 'No name'}</h3>
                    <p className="text-gray-600 flex items-center mt-1">
                      <FiMail className="mr-2" />
                      {selectedUser.email}
                    </p>
                    <div className="mt-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedUser.subscriptionStatus)}`}>
                        {selectedUser.subscriptionStatus || 'inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Account Information</h4>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">User ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 font-mono">{selectedUser.uid}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Created At</dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-center">
                          <FiCalendar className="mr-2 text-gray-400" />
                          {formatDate(selectedUser.createdAt)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Last Login</dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-center">
                          <FiCalendar className="mr-2 text-gray-400" />
                          {formatDate(selectedUser.lastLoginAt)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email Notifications</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedUser.emailNotificationsEnabled !== false ? (
                            <span className="text-green-600">✓ Enabled</span>
                          ) : (
                            <span className="text-red-600">✗ Opted Out</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Subscription Details</h4>
                    <dl className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {selectedUser.subscriptionStatus || 'inactive'}
                        </dd>
                      </div>
                      {selectedUser.stripeCustomerId && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500 flex items-center">
                            <FiCreditCard className="mr-2" />
                            Stripe Customer ID
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 font-mono">
                            {selectedUser.stripeCustomerId}
                          </dd>
                        </div>
                      )}
                      {selectedUser.subscriptionStartDate && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Subscription Start</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(selectedUser.subscriptionStartDate)}
                          </dd>
                        </div>
                      )}
                      {selectedUser.subscriptionEndDate && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Subscription End</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(selectedUser.subscriptionEndDate)}
                          </dd>
                        </div>
                      )}
                      {selectedUser.stripeSubscriptionId && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Stripe Subscription ID</dt>
                          <dd className="mt-1 text-sm text-gray-900 font-mono">
                            {selectedUser.stripeSubscriptionId}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                {/* Additional Fields */}
                {Object.keys(selectedUser).length > 15 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Additional Fields</h4>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                        {JSON.stringify(selectedUser, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Conversations Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase flex items-center">
                      <FiMessageSquare className="mr-2" />
                      Conversations
                    </h4>
                    <button
                      onClick={handleFetchConversations}
                      disabled={loadingConversations}
                      className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {loadingConversations ? (
                        <Spinner size="small" />
                      ) : (
                        <>
                          {showConversations ? <FiChevronUp /> : <FiChevronDown />}
                          {showConversations ? 'Hide' : 'View'} Conversations
                        </>
                      )}
                    </button>
                  </div>

                  {showConversations && (
                    <div className="bg-gray-50 rounded-lg p-4 max-h-80 overflow-y-auto">
                      {conversations.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No conversations found</p>
                      ) : (
                        <div className="space-y-2">
                          {conversations.map((conv) => (
                            <div
                              key={conv.id}
                              className="bg-white rounded-md p-3 border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors"
                              onClick={() => handleViewMessages(conv.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {conv.title || 'Untitled Conversation'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Character: {conv.characterId || 'Bela'} • Status: {conv.status}
                                  </p>
                                </div>
                                <div className="text-xs text-gray-400 ml-2">
                                  {formatDate(conv.updatedAt)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <FiMessageSquare className="mr-2 text-blue-600" />
                Conversation Messages
              </h2>
              <button
                onClick={handleCloseMessages}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            {loadingMessages ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : (
              <div className="p-6">
                {messages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No messages found</p>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-blue-50 ml-8'
                            : 'bg-gray-100 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-semibold ${
                            msg.role === 'user' ? 'text-blue-600' : 'text-gray-600'
                          }`}>
                            {msg.role === 'user' ? 'User' : 'AI'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {msg.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
